import {
  isMappedClientRole,
  primaryUiBucket,
  uiBucketFromClientRole,
} from './roleCapabilities';

const ACCOUNT_ROLE_MARKERS = [
  'manage-account',
  'view-profile',
  'view-consent',
  'delete-account',
  'offline_access',
  'uma_authorization',
];

export function isAccountRole(role) {
  if (!role) return true;
  const lower = String(role).trim().toLowerCase();
  return ACCOUNT_ROLE_MARKERS.some((marker) => lower === marker || lower.startsWith(`${marker}-`));
}

/** Role is known in the capability map (grants a UI bucket). */
export function isAppRole(role) {
  if (!role || isAccountRole(role)) return false;
  return isMappedClientRole(role);
}

/**
 * Normalize any client / display role to Admin | Manager | Agent | Viewer via capability map.
 * Unmapped roles are returned unchanged (caller may treat as no access).
 */
export function normalizeUserRole(role) {
  if (!role) return role;
  const bucket = uiBucketFromClientRole(role);
  return bucket || role;
}

function pickAppRole(roles = []) {
  const candidates = (roles || []).filter((role) => isAppRole(role) && !isAccountRole(role));
  return primaryUiBucket(candidates);
}

/**
 * Roles for this app client only (exact clientId + same app-prefix clients).
 * Avoids promoting Admin from another product's client into this app's UI.
 */
function rolesForAppClient(clientRoles, clientId) {
  if (!clientRoles || typeof clientRoles !== 'object' || Array.isArray(clientRoles)) {
    return [];
  }
  if (!clientId) {
    return flattenClientRoles(clientRoles, clientId);
  }

  const prefix = String(clientId).split('-')[0];
  const keys = Object.keys(clientRoles).filter(
    (key) => key !== 'account' && (key === clientId || key.startsWith(`${prefix}-`))
  );

  const ordered = [
    ...keys.filter((k) => k === clientId),
    ...keys.filter((k) => k !== clientId),
  ];

  const roles = [];
  ordered.forEach((key) => {
    const entry = clientRoles[key];
    if (Array.isArray(entry)) roles.push(...entry);
  });
  return roles;
}

function flattenClientRoles(clientRoles, clientId) {
  if (!clientRoles || typeof clientRoles !== 'object' || Array.isArray(clientRoles)) {
    return [];
  }

  const preferredKeys = [];
  if (clientId) {
    preferredKeys.push(clientId);
    const appPrefix = String(clientId).split('-')[0];
    Object.keys(clientRoles).forEach((key) => {
      if (key !== 'account' && key.startsWith(`${appPrefix}-`)) {
        preferredKeys.push(key);
      }
    });
  }

  const orderedKeys = [
    ...preferredKeys,
    ...Object.keys(clientRoles).filter((key) => key !== 'account' && !preferredKeys.includes(key)),
  ];

  const roles = [];
  orderedKeys.forEach((key) => {
    const entry = clientRoles[key];
    if (Array.isArray(entry)) {
      roles.push(...entry);
    }
  });

  return roles;
}

/**
 * Resolve UI role (Admin / Manager / Agent / Viewer) from Keycloak user payload via capability map.
 * Prefers this app's client roles, then flattened list. Ignores account-level roles for gating.
 */
export function resolveUserRole(user, clientId) {
  if (!user) return null;

  if (user.clientRoles && typeof user.clientRoles === 'object' && !Array.isArray(user.clientRoles)) {
    const scoped = pickAppRole(rolesForAppClient(user.clientRoles, clientId));
    if (scoped) return scoped;

    const fromAll = pickAppRole(flattenClientRoles(user.clientRoles, clientId));
    if (fromAll) return fromAll;
  }

  const clientRolesList = Array.isArray(user.client_roles) ? user.client_roles : [];
  const fromList = pickAppRole(clientRolesList);
  if (fromList) return fromList;

  const hasAccountAccess = clientRolesList.some((role) => {
    const lower = String(role).toLowerCase();
    return lower === 'manage-account' || lower.startsWith('manage-account');
  });
  if (hasAccountAccess) return 'Admin';

  const fallback = clientRolesList.find((role) => !isAccountRole(role));
  return fallback ? normalizeUserRole(fallback) : null;
}

export function normalizeClientRolesList(rawUser, clientId) {
  if (!rawUser) return [];

  if (rawUser.clientRoles && typeof rawUser.clientRoles === 'object' && !Array.isArray(rawUser.clientRoles)) {
    // Prefer app-scoped list for storage; keeps capability checks accurate after login.
    const scoped = rolesForAppClient(rawUser.clientRoles, clientId);
    if (scoped.length) return scoped;
    return flattenClientRoles(rawUser.clientRoles, clientId);
  }

  if (Array.isArray(rawUser.client_roles)) {
    return rawUser.client_roles;
  }

  return [];
}
